/**
 * Copy for the Unmatched proofs tab (S-04, PE-1006), behind `erp_app_proof_of_payment_ingestion_v1`.
 * Mounted as `accounts.unmatchedProofs`.
 *
 * The reason on each row is the server's own sentence and is not in this file: it is rendered as
 * it arrives. The empty-state body sentences are written from FR-U12 and the ticket's wording,
 * since the design file's own text was not available to the build.
 *
 * `detail` is the proof detail drawer (S-06, PE-1010), whose column lead lines and Delivery labels
 * were written without the design file (DL-181 D57). `comparison` is `AmountComparisonStacked`
 * (C-02's stacked form), kept here because the drawer is its first and only caller so far.
 *
 * `attach` is the attach sub-view (S-07, PE-1025). Its sentences are the ticket's (DL-146) except the
 * back control, the search placeholder, the loading line, the past-window and unrecorded-vendor
 * words, the selection announcements, and the not-found and unknown-outcome notices, which the
 * design does not draw and are listed for PE-1009's copy pass.
 *
 * `reject` is the reject dialog (S-08) and `retry` the retry beside the retry note (PE-1026). Their
 * sentences are the ticket's (DL-146) except the summary's document and former-user values, the
 * reject 404, its "no longer needs a decision" 409, its unknown outcome, the generic retry 409,
 * the retry 404 and its unknown outcome, which the design does not draw and are listed for
 * PE-1009's copy pass.
 *
 * `missingFigure` is shared with the Payments table (PE-1007): the same words stand for "there is
 * no figure" on both lists, and in-app copy never carries a dash glyph for it (DL-146).
 */
export default {
  readAccess: 'You have read access to unmatched proofs.',
  setLine: {
    loading: 'Proofs that matched no payment.',
    error: 'The list could not be read.',
    count: '1 proof matched no payment. | {count} proofs matched no payment.',
    rejected: 'Proofs you have rejected. They are retained and stay findable here.',
  },
  announcement: {
    loading: 'Loading unmatched proofs.',
    error: 'Unmatched proofs could not be loaded.',
    needsDecision: '1 proof needs a decision. | {count} proofs need a decision.',
    matching: '1 proof matches the current filters. | {count} proofs match the current filters.',
  },
  filters: {
    search: 'Search sender, vendor or amount',
    family: {
      label: 'Family',
      any: 'Any family',
    },
    status: {
      label: 'Status',
      NeedsDecision: 'Needs a decision',
      Rejected: 'Rejected',
    },
    vendor: {
      label: 'Vendor',
      placeholder: 'Any vendor',
    },
    age: {
      label: 'Age',
      Any: 'Any',
      OverAWeek: 'Over a week',
      OverAMonth: 'Over a month',
    },
  },
  family: {
    BlockedAtConfirm: 'Blocked at confirm',
    Contradicted: 'Contradicted',
    Ambiguous: 'Ambiguous',
    NotMatched: 'Not matched',
    Unreadable: 'Unreadable',
    SenderNotRecognised: 'Sender not recognised',
  },
  columns: {
    family: 'Family',
    reason: 'Reason',
    sender: 'Sender',
    beneficiary: 'Beneficiary / vendor',
    proofAmount: 'Proof amount',
    cashAmount: 'Payment cash amount',
    difference: 'Difference',
    received: 'Received',
    age: 'Age',
    action: 'Action',
  },
  sender: {
    uploadedBy: 'Uploaded by {name}',
  },
  missingFigure: {
    unavailable: 'unavailable',
    none: 'No figure',
  },
  difference: {
    short: 'short',
    over: 'over',
  },
  age: {
    days: 'Under a day | 1 day | {count} days',
    overAMonth: 'over a month',
  },
  rejection: {
    byAndOn: 'Rejected by {name} on {date}',
    by: 'Rejected by {name}',
    on: 'Rejected on {date}',
    plain: 'Rejected',
    reason: 'Reason given: {reason}',
  },
  open: {
    label: 'Open',
    readOnly: 'Open read-only',
    fromSender: 'Open {family} proof from {sender}',
    uploadedBy: 'Open {family} proof uploaded by {name}',
    plain: 'Open {family} proof',
    readOnlyName: '{name}, read-only',
  },
  card: {
    sender: 'Sender',
    beneficiary: 'Beneficiary / vendor',
    proofAmount: 'Proof amount',
    cashAmount: 'Payment cash amount',
    difference: 'Difference',
    received: 'Received',
  },
  states: {
    settled: {
      title: 'Every proof has settled itself',
      description:
        'Every proof received was matched to its payment without anyone touching it. Nothing needs a decision.',
    },
    nothingRejected: {
      title: 'Nothing has been rejected',
      description: 'Proofs you reject are retained and stay findable here.',
    },
    noResults: {
      title: 'No matching proofs',
      description:
        'Nothing matched the filters you have set. That is not the same as nothing being unmatched: clear the filters to see the whole list.',
    },
    error: {
      title: 'Unmatched proofs could not be loaded.',
      retry: 'Retry',
    },
  },
  footer: {
    all: 'Showing all {shown} of {total}. This screen does not paginate: how long it is, is the measure of how often matching is failing upstream.',
    truncated:
      'Showing {shown} of {total} matching proofs. This screen does not paginate: narrow the filters to reach the rest.',
  },
  detail: {
    title: {
      fromSender: 'Proof from {sender}',
      noSender: 'Proof with no sender address',
      uploadedBy: 'Proof uploaded by {name}',
      uploadedByFormerUser: 'Proof uploaded by a former user',
    },
    dialogName: 'Proof detail',
    eyebrow: 'Proof of payment',
    close: 'Close',
    loading: 'Loading the proof',
    error: {
      message: 'This proof could not be loaded.',
      retry: 'Retry',
    },
    extraction: {
      title: 'What we read',
      lead: 'Everything read from this document and its email, and what could not be read.',
      fields: {
        paymentReference: 'Payment reference',
        amount: 'Amount',
        beneficiaryName: 'Beneficiary name',
        beneficiaryAccount: 'Beneficiary account',
        bankTransactionId: 'Bank transaction identifier',
        documentDate: 'Date on the document',
      },
      absent: {
        paymentReference: 'None found on the document',
        amount: 'No amount could be read from this document',
        beneficiaryName: 'No beneficiary name could be read from this document',
        beneficiaryAccount: 'No beneficiary account could be read from this document',
        bankTransactionId: 'No bank transaction identifier could be read from this document',
        documentDate: 'No date could be read from this document',
      },
      notRead: {
        StoppedAtSenderGate: 'Could not be read: the reading stopped at the sender gate',
        NotCompleted: 'Could not be read: the reading did not complete',
      },
      fromEmail: 'From the email, not the document',
    },
    retryNote: {
      stalledAtGate:
        'The reading stalled at the allow-list gate, not on the document, so add {sender} to the allow list and retry. Nothing needs to be resent.',
      reread: 'The stored document is read again when you retry. Nothing needs to be resent.',
      readAndUnmatched:
        'This document was read successfully. It simply cannot be matched, so there is nothing to retry and no reason to ask the client to send it again.',
      readAndBlocked:
        'This document was read and matched. The confirmation was blocked, so reading it again changes nothing.',
      cannotChange:
        'Reading this document again cannot change the outcome, so there is nothing to retry.',
      readingAgain:
        'Reading this proof again from the stored document. Nothing needs to be resent.',
      senderFallback: 'the sender',
    },
    delivery: {
      title: 'Delivery',
      subtitle: 'How the proof reached us',
      sender: 'Sender',
      envelopeSender: 'Envelope sender',
      received: 'Received',
      age: 'Age',
      spfDmarc: 'SPF and DMARC',
      allowList: 'Sender on the allow list',
      uploadedBy: 'Uploaded by',
      uploadedAt: 'Uploaded at',
      passed: 'Passed',
      failed: 'Failed',
      yes: 'Yes',
      no: 'No',
      notRecorded: 'Not recorded',
      noSender: 'No single sender address',
      formerUser: 'A former user',
    },
    document: {
      download: 'Download the stored document',
      fileName: 'the stored document',
      received: 'Received {when}',
      notAvailable: 'The stored document is not available.',
      failed: 'The download could not be started. Try again in a moment.',
      unreachable:
        'The document store cannot be reached, so the file cannot be fetched. What we read and the comparison above are unaffected.',
    },
    footer: {
      readAccess:
        'Read access: this proof reads in full and every figure is here. Attaching and rejecting are not available to you.',
      noteBlocked:
        'Attach it to another payment, or reject this proof. Nothing here changes what the bank has already done.',
      readOnly:
        'Mobile is read-only for resolving. Comparing a document against a money figure on a phone, with no reversal available, is not a decision the platform should invite: the proof reads in full and resolves on a larger screen.',
    },
  },
  comparison: {
    title: 'The comparison',
    lead: 'What the proof says against what the payment is.',
    proofSays: 'The proof says',
    paymentCash: "The payment's cash amount ({reference})",
    paymentCashUnreferenced: "The payment's cash amount",
    difference: 'Difference',
    unavailable: 'unavailable',
    notComputed: 'Not computed',
    ProofHigher: '{amount} more on the proof than on the payment.',
    ProofLower: '{amount} less on the proof than on the payment.',
    Equal: 'The two agree to the cent.',
    noProofAmount:
      'No amount could be read from this document, so no difference is computed and none is implied.',
    noPaymentCash:
      "The payment's cash amount is unavailable, so no difference is computed and none is implied.",
    noPayment: {
      title: 'No payment was identified',
      body: 'There is nothing to compare against. A computed R 0.00 would claim that two amounts agree when only one exists.',
    },
  },
  attach: {
    action: 'Attach to a payment',
    title: 'Attach this proof to a payment',
    back: 'Back to the proof',
    candidates: {
      title: 'Payments awaiting proof',
      lead: 'Every payment still waiting on a proof, however old. The thirty-day window does not apply here: past it a payment can only be matched by hand, and this is by hand.',
      closing:
        'One document settles one payment: no multi-select, and no create-a-payment escape hatch. Nothing is chosen for you.',
      groupLabel: 'Choose the payment this proof settles',
      search: 'Search vendor or amount',
      loading: 'Loading the payments awaiting proof',
      truncated: 'Showing the 500 oldest payments awaiting proof. Search to find one not shown.',
      noResults: 'No payment awaiting proof matches that search.',
      empty: {
        title: 'No payment is awaiting a proof',
        description:
          'There is nothing in this organisation for this document to settle. If it is not evidence of one of your payments, reject it.',
      },
      error: {
        message: 'The payments awaiting proof could not be loaded.',
        retry: 'Retry',
      },
    },
    candidate: {
      waiting: 'awaiting proof under a day | awaiting proof 1 day | awaiting proof {count} days',
      pastWindow: 'Past the matching window.',
      vendorUnrecorded: 'Vendor not recorded',
      doesNotFit:
        'The proof names a different beneficiary from this vendor, so it cannot settle this payment.',
    },
    comparison: {
      lead: 'Recomputed against whichever candidate is selected, before anything is committed.',
      nothingSelected: {
        title: 'Nothing selected yet',
        body: 'Choose a payment on the left and this comparison recomputes against it. The point of attaching here rather than in a modal is that you see the difference before you commit to it.',
      },
    },
    footer: {
      noteNothingSelected: 'Nothing is preselected: choosing the payment is the decision.',
      noteSelected:
        'Attaching runs the confirm-time validity gate. If that gate fails the attach still stands and the confirmation does not, and this proof becomes Blocked at confirm.',
      cancel: 'Cancel',
      confirm: 'Attach and confirm',
    },
    flag: {
      amountsDisagree:
        'The amounts do not agree, so this confirmation will be flagged as confirmed on a disagreeing amount.',
      noProofAmount:
        'No amount could be read from the proof, so this confirmation will be flagged as confirmed on a disagreeing amount.',
    },
    selected: '{vendor}, {reference}, selected.',
    selectionCleared: '{reference} is not in these results, so nothing is selected.',
    confirmed: 'Proof attached. {reference} is confirmed.',
    notice: {
      changed: 'This proof or the payment changed while you were choosing. Nothing was attached.',
      beneficiaryContradicts:
        'This proof names a different beneficiary from that vendor, so it was not attached.',
      returnChoiceMissing:
        'That payment owes a return choice that was never recorded, so this proof cannot confirm it. Nothing was attached.',
      notAttached: 'Nothing was attached. Try again.',
      forbidden: 'You do not have permission to attach proofs.',
      notFound: 'This proof or the payment could not be found. Nothing was attached.',
      unknown:
        'It could not be confirmed whether this proof was attached. Close the proof and open it again before trying again.',
    },
  },
  reject: {
    action: 'Reject this proof',
    title: 'Reject this proof?',
    description:
      'Rejecting takes the document out of the decision list. It is retained, never deleted, and stays findable under the Rejected filter.',
    summary: {
      label: 'The proof being rejected',
      document: 'Document',
      documentValue: 'Proof of payment · {type}',
      sender: 'Sender',
      uploadedByFormerUser: 'Uploaded by a former user',
      received: 'Received',
      proofAmount: 'Proof amount',
      beneficiary: 'Beneficiary / vendor',
      family: 'Family',
      noFamily: 'None',
    },
    reason: {
      label: 'Why is this proof being rejected?',
      required: 'A reason is required before this proof can be rejected.',
    },
    confirm: 'Reject this proof',
    cancel: 'Keep it in the list',
    rejected: 'Proof rejected. It is kept under the Rejected filter.',
    notice: {
      notRejected: 'This proof was not rejected. Try again.',
      changed: 'This proof changed before it could be rejected. It was not rejected.',
      noLongerNeedsDecision:
        'This proof changed before it could be rejected and no longer needs a decision. It was not rejected.',
      forbidden: 'You do not have permission to reject proofs.',
      notFound: 'This proof could not be found. It was not rejected.',
      unknown:
        'It could not be confirmed whether this proof was rejected. Close the proof and open it again before trying again.',
    },
  },
  retry: {
    action: 'Retry the reading',
    notice: {
      senderStillNotAllowed:
        '{sender} is still not on your allow list. Add it in organisation settings, then retry the reading.',
      alreadyReadingAgain: 'This proof is already being read again.',
      noLongerAvailable: 'This proof can no longer be read again.',
      notRetried: 'This proof changed before the reading could be restarted. Try again.',
      notRestarted: 'The reading could not be restarted. Try again.',
      forbidden: 'You do not have permission to retry the reading.',
      notFound: 'This proof could not be found. The reading was not restarted.',
      unknown:
        'It could not be confirmed whether the reading was restarted. Close the proof and open it again before trying again.',
    },
  },
};
